import { useEffect, useState } from 'react';
import {
  BUSINESS_PLAN_FIELDS,
  entryKindMeta,
  igpeLevelLabel,
  projectLevelLabel,
  viabilityLabel,
  type BusinessModelCanvas,
  type BusinessPlan,
  type DiagnosticReport,
  type Ideation,
  type Project,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  buildBmcPdfHtml,
  buildBusinessPlanPdfHtml,
  buildConclusionReportPdfHtml,
  openPrintablePdf,
} from '@/lib/exportPdf';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';

interface FeasRow {
  ideation_id: string;
  score_final: number;
  viability: string;
  report_results?: string;
  report_strengths?: string;
  report_weaknesses?: string;
  report_recommendations?: string;
}

interface Props {
  activeIdeationId: string | null;
  onSelect?: (ideation: Ideation) => void;
  refreshKey?: number;
}

function projectMeta(idea: Ideation, zone: string): Record<string, string> {
  return {
    'Intitulé projet': idea.business_idea,
    Domaine: idea.domain || '—',
    Zone: zone || '—',
    Code: idea.solution_code ?? idea.id.slice(0, 8),
  };
}

function openBmcPdf(bmc: BusinessModelCanvas, idea: Ideation, zone: string) {
  openPrintablePdf(
    `Business Model Canvas — ${idea.business_idea}`,
    buildBmcPdfHtml({
      meta: projectMeta(idea, zone),
      blocks: {
        value_propositions: String(bmc.value_propositions ?? ''),
        customer_segments: String(bmc.customer_segments ?? ''),
        channels: String(bmc.channels ?? ''),
        customer_relationships: String(bmc.customer_relationships ?? ''),
        revenue_streams: String(bmc.revenue_streams ?? ''),
        key_resources: String(bmc.key_resources ?? ''),
        key_activities: String(bmc.key_activities ?? ''),
        key_partners: String(bmc.key_partners ?? ''),
        cost_structure: String(bmc.cost_structure ?? ''),
      },
    }),
  );
}

function openBusinessPlanPdf(bp: BusinessPlan, idea: Ideation, zone: string) {
  const content = (bp.content ?? {}) as Record<string, unknown>;
  const bySection = new Map<string, { label: string; value: string }[]>();
  for (const f of BUSINESS_PLAN_FIELDS) {
    const list = bySection.get(f.section) ?? [];
    list.push({ label: f.label, value: String(content[f.key] ?? '') });
    bySection.set(f.section, list);
  }
  openPrintablePdf(
    `Business Plan — ${idea.business_idea}`,
    buildBusinessPlanPdfHtml({
      meta: projectMeta(idea, zone),
      sections: [...bySection.entries()].map(([title, fields]) => ({ title, fields })),
    }),
  );
}

export function ConceptionSynthesis({ activeIdeationId, onSelect, refreshKey = 0 }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [ideas, setIdeas] = useState<Ideation[]>([]);
  const [projects, setProjects] = useState<Record<string, Project>>({});
  const [diagnostics, setDiagnostics] = useState<Record<string, DiagnosticReport>>({});
  const [feasibility, setFeasibility] = useState<Record<string, FeasRow>>({});
  const [bmcs, setBmcs] = useState<Record<string, BusinessModelCanvas>>({});
  const [bps, setBps] = useState<Record<string, BusinessPlan>>({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Section synthèse pliée / dépliée */
  const [sectionOpen, setSectionOpen] = useState(true);
  /** Détails d’une carte projet (une seule ouverte à la fois) */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [collapseInitialized, setCollapseInitialized] = useState(false);

  async function load() {
    if (!profile) return;
    setLoading(true);    const { data: retained } = await supabase
      .from('ideations')
      .select('*')
      .eq('user_id', profile.id)
      .in('status', ['retained', 'project_linked'])
      .order('created_at', { ascending: false });
    const list = (retained ?? []) as Ideation[];
    setIdeas(list);
    const ids = list.map((i) => i.id);
    if (ids.length === 0) {
      setProjects({});
      setDiagnostics({});
      setFeasibility({});
      setBmcs({});
      setBps({});
      setLoading(false);
      return;
    }
    const [proj, diag, feas, bmc, bp] = await Promise.all([
      supabase.from('projects').select('*').in('ideation_id', ids),
      supabase.from('diagnostic_reports').select('*').in('ideation_id', ids),
      supabase
        .from('feasibility_scores')
        .select(
          'ideation_id,score_final,viability,report_results,report_strengths,report_weaknesses,report_recommendations',
        )
        .in('ideation_id', ids),
      supabase.from('business_model_canvases').select('*').in('ideation_id', ids),
      supabase.from('business_plans').select('*').in('ideation_id', ids),
    ]);
    setProjects(
      Object.fromEntries(((proj.data ?? []) as Project[]).map((p) => [p.ideation_id, p])),
    );
    setDiagnostics(
      Object.fromEntries(((diag.data ?? []) as DiagnosticReport[]).map((d) => [d.ideation_id, d])),
    );
    setFeasibility(
      Object.fromEntries(((feas.data ?? []) as FeasRow[]).map((f) => [f.ideation_id, f])),
    );
    setBmcs(
      Object.fromEntries(((bmc.data ?? []) as BusinessModelCanvas[]).map((b) => [b.ideation_id, b])),
    );
    setBps(Object.fromEntries(((bp.data ?? []) as BusinessPlan[]).map((b) => [b.ideation_id, b])));
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshKey drives reload
  }, [profile, refreshKey]);

  useEffect(() => {
    if (loading || collapseInitialized) return;
    if (ideas.length >= 2) {
      setSectionOpen(false);
      setExpandedId(activeIdeationId);
    } else if (ideas.length === 1) {
      setSectionOpen(true);
      setExpandedId(ideas[0]?.id ?? null);
    }
    setCollapseInitialized(true);
  }, [loading, ideas, activeIdeationId, collapseInitialized]);

  function toggleCard(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  async function removeProject(idea: Ideation) {
    if (!window.confirm(`Supprimer « ${idea.business_idea} » ? Le projet reste en archive 6 mois.`)) {
      return;
    }
    setBusyId(idea.id);
    try {
      const { error: rpcErr } = await supabase.rpc('soft_delete_ideation', {
        p_ideation_id: idea.id,
      });
      if (rpcErr) throw rpcErr;
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Suppression impossible');
    } finally {
      setBusyId(null);
    }
  }

  if (!profile) return null;

  const projectCountLabel =
    ideas.length === 0
      ? 'Aucun projet'
      : ideas.length === 1
        ? '1 projet'
        : `${ideas.length} projets`;

  return (
    <section className={`u-card u-synth-section${sectionOpen ? ' is-open' : ' is-collapsed'}`}>
      <button
        type="button"
        className="u-synth-section-toggle"
        onClick={() => setSectionOpen((o) => !o)}
        aria-expanded={sectionOpen}
      >
        <span className="u-synth-section-toggle-main">
          <span className="u-synth-chevron" aria-hidden>
            {sectionOpen ? '▼' : '▶'}
          </span>
          <span>
            <strong>Synthèse générale des projets</strong>
            <span className="u-synth-section-count">{projectCountLabel}</span>
          </span>
        </span>
        <span className="u-synth-section-hint">{sectionOpen ? 'Replier' : 'Déplier'}</span>
      </button>

      {sectionOpen ? (
        <>
          <p className="muted u-synth-section-desc">
            ID, intitulé, domaine, besoin, solution, zone, statut de viabilité, Business Model et
            Business Plan en PDF. Cliquez sur un projet pour voir le détail. Suppression = archive 6
            mois (quota 12 projets/an).
          </p>
          {loading ? <ListSkeleton rows={3} /> : null}
          {!loading && ideas.length === 0 ? (
            <p className="muted">Aucun projet pour le moment.</p>
          ) : null}
          <div className="u-synth-list">
            {ideas.map((idea) => {
              const meta = entryKindMeta(idea.entry_kind ?? 'assisted');
              const project = projects[idea.id];
              const diag = diagnostics[idea.id];
              const feas = feasibility[idea.id];
              const bmc = bmcs[idea.id];
              const bp = bps[idea.id];
              const isActive = idea.id === activeIdeationId;
              const isExpanded = expandedId === idea.id;
              const viability = project?.viability ?? feas?.viability ?? 'pending';
              const level =
                project?.project_level ??
                (idea.entry_kind === 'existing_activity' ? 'en_cours' : 'nouveau');
              const projectId =
                project?.id ?? idea.project_id ?? idea.solution_code ?? idea.id.slice(0, 8);
              const zone = [idea.city, idea.country_code].filter(Boolean).join(', ');
              const problem = (idea.problems ?? []).join(' · ') || '—';
              const solution = (idea.solutions ?? []).join(' · ') || idea.business_idea;

              return (
                <div
                  key={idea.id}
                  className={`u-synth-card${isActive ? ' is-active' : ''}${isExpanded ? ' is-expanded' : ''}`}
                >
                  <div className="u-synth-card-head">
                    <button
                      type="button"
                      className="u-synth-card-toggle"
                      onClick={() => toggleCard(idea.id)}
                      aria-expanded={isExpanded}
                    >
                      <span className="u-synth-chevron" aria-hidden>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span>
                        <strong>{idea.business_idea}</strong>
                        <div className="u-synth-meta">
                          ID {projectId} · {idea.solution_code ?? '—'} · {meta.title} · Niveau{' '}
                          {projectLevelLabel(level)}
                        </div>
                      </span>
                    </button>
                    <div className="u-synth-actions">
                      {onSelect ? (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => onSelect(idea)}
                        >
                          Reprendre
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={busyId === idea.id}
                        onClick={() => void removeProject(idea)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>

                  <div className="u-synth-chips">
                    <span
                      className={`chip ${viability === 'viable' ? 'ok' : viability === 'not_viable' ? 'warn' : ''}`}
                    >
                      {viabilityLabel(viability)}
                    </span>
                    {idea.entry_kind === 'existing_activity' ? (
                      <span className={`chip ${diag ? 'ok' : 'warn'}`}>
                        {diag
                          ? `Diagnostic IGPE ${Number(diag.igpe_score).toFixed(0)}/100 (${igpeLevelLabel(diag.igpe_level)})`
                          : 'Diagnostic à faire'}
                      </span>
                    ) : (
                      <span className={`chip ${feas ? 'ok' : 'warn'}`}>
                        {feas ? `Faisabilité ${feas.score_final}` : 'Faisabilité à faire'}
                      </span>
                    )}
                    <span className={`chip ${bmc?.is_complete ? 'ok' : 'warn'}`}>
                      {bmc ? (bmc.is_complete ? 'BMC complet' : 'BMC brouillon') : 'BMC manquant'}
                    </span>
                    <span className={`chip ${bp?.is_complete ? 'ok' : 'warn'}`}>
                      {bp ? (bp.is_complete ? 'BP complet' : 'BP brouillon') : 'BP manquant'}
                    </span>
                  </div>

                  {isExpanded ? (
                    <>
                      <dl className="u-synth-dl">
                        <div>
                          <dt>Domaine</dt>
                          <dd>{idea.domain || '—'}</dd>
                        </div>
                        <div>
                          <dt>Zone d’opération</dt>
                          <dd>{zone || '—'}</dd>
                        </div>
                        <div>
                          <dt>Besoin / problème</dt>
                          <dd>{problem}</dd>
                        </div>
                        <div>
                          <dt>Solution entrepreneuriale</dt>
                          <dd>{solution}</dd>
                        </div>
                        <div>
                          <dt>Statut projet</dt>
                          <dd>{viabilityLabel(viability)}</dd>
                        </div>
                      </dl>

                      <div className="u-synth-downloads">
                        {bmc ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => openBmcPdf(bmc, idea, zone)}
                          >
                            PDF Business Model
                          </button>
                        ) : null}
                        {bp ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => openBusinessPlanPdf(bp, idea, zone)}
                          >
                            PDF Business Plan
                          </button>
                        ) : null}
                        {feas?.report_results ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() =>
                              openPrintablePdf(
                                `Rapport d’étude de faisabilité — ${idea.business_idea}`,
                                buildConclusionReportPdfHtml({
                                  meta: projectMeta(idea, zone),
                                  viability:
                                    (feas.viability as 'viable' | 'not_viable') || 'pending',
                                  results: feas.report_results || '',
                                  strengths: (feas.report_strengths || '')
                                    .split('\n')
                                    .filter(Boolean),
                                  weaknesses: (feas.report_weaknesses || '')
                                    .split('\n')
                                    .filter(Boolean),
                                  recommendations: (feas.report_recommendations || '')
                                    .split('\n')
                                    .filter(Boolean),
                                }),
                              )
                            }
                          >
                            PDF rapport faisabilité
                          </button>
                        ) : null}
                        {diag?.report_results || diag?.summary ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() =>
                              openPrintablePdf(
                                `Rapport de diagnostic — ${idea.business_idea}`,
                                buildConclusionReportPdfHtml({
                                  meta: projectMeta(idea, zone),
                                  viability:
                                    Number(diag.igpe_score) >= 50 ? 'viable' : 'not_viable',
                                  results: diag.report_results || diag.summary,
                                  strengths: (diag.report_strengths || '')
                                    .split('\n')
                                    .filter(Boolean),
                                  weaknesses: (diag.report_weaknesses || '')
                                    .split('\n')
                                    .filter(Boolean),
                                  recommendations: (diag.report_recommendations || '')
                                    .split('\n')
                                    .filter(Boolean),
                                }),
                              )
                            }
                          >
                            PDF rapport diagnostic
                          </button>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      ) : !loading && ideas.length > 0 ? (
        <p className="muted u-synth-collapsed-hint">
          Section repliée — {projectCountLabel}. Dépliez pour consulter les fiches et les PDF.
        </p>
      ) : loading ? (
        <ListSkeleton rows={2} />
      ) : null}
    </section>
  );
}
